import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';

const AnalyticsKpi = ({ icon: Icon, label, value, sub, testId, tone }) => (
  <Card className="jw-card p-4 relative overflow-hidden" data-testid={testId}>
    {tone === 'brand' && <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full opacity-20 blur-2xl jw-gradient-bg" />}
    <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--jw-text-muted)' }}>
      <Icon className="w-4 h-4" /> {label}
    </div>
    <p className="font-display text-2xl sm:text-3xl font-bold mt-2">{value}</p>
    {sub && <p className="text-[11px] mt-1" style={{ color: 'var(--jw-text-subtle)' }}>{sub}</p>}
  </Card>
);

export const AnalyticsSection = () => {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/analytics', { params: { days } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || !data) return <div className="jw-card p-10 text-center" style={{ color: 'var(--jw-text-muted)' }}>Loading analytics…</div>;

  const k = data.kpis;
  const conv = data.conversion;
  const funnel = data.funnel;
  const maxFunnel = funnel[0]?.sessions || 1;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-lg">Analytics</h3>
          <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>Visitor sessions, cart activity, and conversion — anonymous sessions bind to registered users on sign-in.</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-36" data-testid="admin-analytics-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24 hours</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <AnalyticsKpi icon={Users} label="Unique visitors" value={k.unique_sessions.toLocaleString()} sub={`${k.unique_ips} unique IPs`} testId="admin-analytics-kpi-visitors" tone="brand" />
        <AnalyticsKpi icon={LayoutDashboard} label="Page views" value={k.page_views.toLocaleString()} testId="admin-analytics-kpi-views" />
        <AnalyticsKpi icon={ShoppingBag} label="Add to cart" value={k.add_to_cart.toLocaleString()} sub={`${conv.visitor_to_cart_pct}% of visitors`} testId="admin-analytics-kpi-atc" />
        <AnalyticsKpi icon={Receipt} label="Orders placed" value={k.checkout_completes.toLocaleString()} sub={`${conv.visitor_to_order_pct}% conversion`} testId="admin-analytics-kpi-orders" />
        <AnalyticsKpi icon={UserCog} label="Signed-in visitors" value={k.signed_in_visitors.toLocaleString()} testId="admin-analytics-kpi-signed" />
        <AnalyticsKpi icon={Users} label="Total customers" value={k.customers_total.toLocaleString()} sub={`${k.customers_converted_from_anon} converted from anon`} testId="admin-analytics-kpi-customers" />
        <AnalyticsKpi icon={Send} label="Registrations" value={k.registers.toLocaleString()} testId="admin-analytics-kpi-registers" />
        <AnalyticsKpi icon={Shield} label="Logins" value={k.logins.toLocaleString()} testId="admin-analytics-kpi-logins" />
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4">
        <Card className="jw-card p-4" data-testid="admin-analytics-funnel">
          <h4 className="font-semibold text-sm mb-3">Conversion funnel</h4>
          <div className="space-y-2">
            {funnel.map((f, i) => {
              const width = Math.max(6, (f.sessions / maxFunnel) * 100);
              return (
                <div key={f.step}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span style={{ color: 'var(--jw-text-muted)' }}>{f.step}</span>
                    <span>
                      <b>{f.sessions.toLocaleString()}</b>
                      {i > 0 && <span className="ml-2" style={{ color: 'var(--jw-text-subtle)' }}>{f.rate_from_prev}%</span>}
                    </span>
                  </div>
                  <div className="h-6 rounded-md overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
                    <div className="h-full jw-gradient-bg" style={{ width: `${width}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t" style={{ borderColor: 'var(--jw-border)' }}>
            <div><p className="text-[10px]" style={{ color: 'var(--jw-text-muted)' }}>Visitor → Cart</p><p className="font-semibold">{conv.visitor_to_cart_pct}%</p></div>
            <div><p className="text-[10px]" style={{ color: 'var(--jw-text-muted)' }}>Cart → Checkout</p><p className="font-semibold">{conv.cart_to_checkout_pct}%</p></div>
            <div><p className="text-[10px]" style={{ color: 'var(--jw-text-muted)' }}>Checkout → Order</p><p className="font-semibold">{conv.checkout_to_order_pct}%</p></div>
          </div>
        </Card>

        <Card className="jw-card p-4" data-testid="admin-analytics-toppages">
          <h4 className="font-semibold text-sm mb-3">Top pages</h4>
          {data.top_pages.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>No visits yet in this range.</p>
          ) : (
            <div className="space-y-2">
              {data.top_pages.map((p) => (
                <div key={p.path} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-xs truncate max-w-[220px]" title={p.path}>{p.path}</span>
                  <span className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
                    <b>{p.views}</b> views · {p.unique} sessions
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="jw-card p-4" data-testid="admin-analytics-timeseries">
        <h4 className="font-semibold text-sm mb-3">Visitors per day</h4>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={data.timeseries}>
              <CartesianGrid stroke="var(--jw-border)" strokeDasharray="2 2" />
              <XAxis dataKey="date" stroke="var(--jw-text-subtle)" fontSize={11} />
              <YAxis stroke="var(--jw-text-subtle)" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'var(--jw-panel)', border: '1px solid var(--jw-border)', color: 'var(--jw-text)' }} />
              <Bar dataKey="unique" name="Unique sessions" fill="#f472b6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="views" name="Page views" fill="#fb923c" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
};

export default AnalyticsSection;
